import { Link } from 'react-router-dom';
import { Shield, Heart, Users, Target, ChevronRight } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_vets-intake-demo/artifacts/e4mtecfo_Untitled%20design%20%2892%29.png";

// Professional veteran images
const IMAGES = {
  veteranGroup: "https://images.unsplash.com/photo-1763359838113-823ac5e75440?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHw0fHx2ZXRlcmFucyUyMGhlbHBpbmclMjBzdXBwb3J0JTIwZmFtaWx5JTIwbWlsaXRhcnl8ZW58MHx8fHwxNzczOTY5MzY3fDA&ixlib=rb-4.1.0&q=85",
  veteranSupport: "https://images.pexels.com/photos/7467907/pexels-photo-7467907.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  familyReunion: "https://images.unsplash.com/photo-1674049406385-08fb37eeebd4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwzfHx2ZXRlcmFucyUyMGhlbHBpbmclMjBzdXBwb3J0JTIwZmFtaWx5JTIwbWlsaXRhcnl8ZW58MHx8fHwxNzczOTY5MzY3fDA&ixlib=rb-4.1.0&q=85"
};

export const AboutPage = () => {
  const values = [
    {
      icon: Heart,
      title: 'Compassion',
      description: 'We understand the struggles veterans face because we have been there. Every veteran deserves patience and understanding.'
    },
    {
      icon: Shield,
      title: 'Integrity',
      description: 'We provide honest guidance and never make promises we cannot keep. Your trust is our foundation.'
    },
    {
      icon: Users,
      title: 'Service',
      description: 'Just as you served your country, we are committed to serving you. Your success is our mission.'
    },
    {
      icon: Target,
      title: 'Excellence',
      description: 'We stay current on VA policies and procedures to provide you with the best possible guidance.'
    }
  ];

  return (
    <div className="page-transition" data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-wv-blue py-20" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-wv-red text-lg font-semibold mb-4 tracking-wide uppercase">
              About Us
            </p>
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6 uppercase tracking-wide">
              Our Mission &<br />Our Story
            </h1>
            <p className="text-xl text-white/80 leading-relaxed">
              Worthy Vets Service Group was founded with one purpose: to ensure every 
              veteran receives the support they deserve when navigating the VA system.
            </p>
          </div>
        </div>
      </section>

      {/* Founder Story Section */}
      <section className="py-20 bg-paper-white" data-testid="founder-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src={IMAGES.veteranSupport}
                alt="Veterans supporting each other"
                className="rounded-lg shadow-xl w-full"
              />
            </div>
            <div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-wv-blue mb-6">
                Why We Started Worthy Vets
              </h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  After serving in the military, many veterans return home to face a new 
                  battle: navigating the complex and often frustrating VA disability claims 
                  process. Too many veterans feel lost, overwhelmed, or simply give up on 
                  getting the benefits they've earned.
                </p>
                <p>
                  Worthy Vets Service Group was created because we believe no veteran should 
                  have to face this system alone. We've seen firsthand how confusing the 
                  process can be—the paperwork, the medical evidence requirements, the long 
                  wait times, and the appeals when claims are denied.
                </p>
                <p>
                  Our founder experienced these challenges personally and recognized that 
                  many veterans simply need someone in their corner—someone who understands 
                  the system and can help guide them through it step by step.
                </p>
                <p className="font-semibold text-wv-blue">
                  That's why we're here. Because every veteran is worthy of the benefits 
                  they earned through their service.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-white" data-testid="mission-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img 
            src={LOGO_URL} 
            alt="Worthy Vets Service Group" 
            className="h-28 w-auto mx-auto mb-8"
            loading="lazy"
          />
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-wv-blue mb-8">
            Our Mission
          </h2>
          <p className="text-2xl text-gray-700 leading-relaxed mb-8">
            To provide compassionate, knowledgeable guidance to veterans navigating the 
            VA disability claims process, ensuring they receive the benefits and recognition 
            they have rightfully earned through their service to our nation.
          </p>
          <div className="h-1 w-32 bg-wv-red mx-auto rounded-full" />
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-paper-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-wv-blue mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              These values guide everything we do at Worthy Vets Service Group.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-8 border-2 border-gray-100 hover:border-service-gold transition-colors"
                data-testid={`value-card-${index}`}
              >
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-wv-red rounded-lg flex items-center justify-center flex-shrink-0">
                    <value.icon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl font-bold text-wv-blue mb-3">
                      {value.title}
                    </h3>
                    <p className="text-lg text-gray-600 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20 bg-white" data-testid="what-we-do-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-wv-blue mb-6">
                What We Do
              </h2>
              <div className="space-y-6">
                <div className="border-l-4 border-wv-red pl-6">
                  <h3 className="font-heading text-xl font-bold text-wv-blue mb-2">
                    VA Disability Claims Assistance
                  </h3>
                  <p className="text-lg text-gray-600">
                    We guide you through filing new claims, requesting increases, or 
                    navigating the appeals process.
                  </p>
                </div>
                <div className="border-l-4 border-wv-red pl-6">
                  <h3 className="font-heading text-xl font-bold text-wv-blue mb-2">
                    DBQ & Nexus Letter Support
                  </h3>
                  <p className="text-lg text-gray-600">
                    We help you understand and obtain the medical documentation that can 
                    make or break your claim.
                  </p>
                </div>
                <div className="border-l-4 border-wv-red pl-6">
                  <h3 className="font-heading text-xl font-bold text-wv-blue mb-2">
                    VA Process Education
                  </h3>
                  <p className="text-lg text-gray-600">
                    We explain the process in plain language so you understand what's 
                    happening and what to expect.
                  </p>
                </div>
              </div>
              <div className="mt-8">
                <Link
                  to="/services"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  View All Services
                  <ChevronRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
            <div>
              <img
                src={IMAGES.familyReunion}
                alt="Veteran receiving support"
                className="rounded-lg shadow-xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-wv-blue" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Let Us Help You
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            You've already served your country. Now let us serve you. Take the first 
            step toward getting the benefits you deserve.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/intake"
              className="btn-primary inline-flex items-center justify-center gap-2"
              data-testid="about-cta-primary"
            >
              Start Your Intake
              <ChevronRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="btn-secondary inline-flex items-center justify-center"
              data-testid="about-cta-secondary"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
